import { useEffect, useState, useMemo } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  updateColumnMapping,
  getDataVisualization,
  getReference,
  getPlantReadiness,
} from "../../network/cipAPI";

import DupontRadio from "../../components/DupontRadio";
import WPInput from "../../packages/WPInput";
import WPDropdown from "../../packages/WPDropdown";
import WPTertiaryButton from "../../packages/WPTertiaryButton";
import { CopyIcon, FTNormProIcon } from "../../assets/icons";
import DataVisualizationTable from "./DataVisualizationTable";
import IconWithTooltip from "../../common/styles/components/headings/IconWithTooltip";
import createNotification, {
  SUCCESS_NOTIFICATION,
  ERROR_NOTIFICATION,
  WARNING_NOTIFICATION,
  INFO_NOTIFICATION,
} from "../../components/Notifications";

import { UpdateSetWithKey, getDatasetDetails } from "../../utils/Utils";
import {
  COLUMN_OPTIONS,
  NORMALIZATION_COLUMN_OPTIONS,
  TRAIN_OPTIONS,
} from "./columns.config";
import { CheckCircleIcon, InfoCircle } from "../../assets/icons";

import {
  PASS_STAGE_KEYS,
  NORMALIZATION_TYPES,
} from "../../constants/global.constants";
import { COLUMN_MAPPING_ERROR_KEYS } from "../../constants/global.constants";
import CircularLoader from "../../common/styles/components/circularLoader";
import ImportColumnMapping from "./importColumnMapping";
import "./index.scss";

const ColumnSelection = ({
  columnOptions,
  setColumnOptions,
  fileId,
  mappedColumn,
  setMappedColumn,
  mappedReference,
  setTempReferenceValues,
  isEdit = false,
  setDatasetDetails,
  plantName,
  waterType,
  selectedRadioColumn,
  setSelectedRadioColumn,
  setMappedReference,
  setIsNormalizationColumnFilled,
  config,
  selectedTrain,
  setMandatoryComponentsStatus,
  normSelectionType,
  ownerUserName,
}) => {
  const { t } = useTranslation();

  const {
    flow,
    pressure,
    feed_quality,
    permeate_quality,
    temperature,
    file_id,
    token,
  } = useSelector((state) => state);

  const unitSettings = useMemo(
    () => ({
      flow,
      pressure,
      feed_quality,
      permeate_quality,
      temperature,
    }),
    [flow, pressure, feed_quality, permeate_quality, temperature],
  );

  const columns = useMemo(
    () => COLUMN_OPTIONS(t, unitSettings),
    [t, unitSettings],
  );
  const normalizedColumns = useMemo(
    () => NORMALIZATION_COLUMN_OPTIONS(t, unitSettings),
    [t, unitSettings],
  );

  const [trainscolumns, setTrainscolumns] = useState([]);
  const [showDataVisualizationPopup, setshowDataVisualizationPopup] =
    useState(false);
  const [dataVisualisationRow, setDataVisualisationRow] = useState([]);
  const [dataVisualisationEnabled, setDataVisualisationEnabled] =
    useState(true);
  const [selectedColumns, setSelectedColumns] = useState({
    sel_train: 1,
    sel_pass: 1,
    sel_stage: 1,
  });
  const [changedSelectedColumns, setChangedSelectedColumns] = useState({});
  const [changedPassStageFields, setChangedPassStageFields] = useState({});
  const [dropdownColumns, setDropdownColumns] = useState(columnOptions);
  const [showOverLayLoader, setShowOverLayLoader] = useState(false);
  const [normalizationOption, setNormalizationOption] = useState("ftnorm");
  const [normalizationChanged, setNormalizationChanged] = useState(false);
  const [showImportColumnMappingPopup, setShowImportColumnMappingPopup] =
    useState(false);
  const [trainsWithColSelection, setTrainsWithColSelection] = useState([]);

  const getOptimizedPayload = (
    mappedColumn,
    changedSelectedColumns,
    changedPassStageFields,
    selectedColumns,
  ) => {
    if (mappedColumn) {
      const changedKeys = Object.keys(changedSelectedColumns);
      const passOrStageChanged = PASS_STAGE_KEYS.some((key) =>
        changedKeys.includes(key),
      );
      return {
        ...changedSelectedColumns,
        ...(passOrStageChanged ? changedPassStageFields : {}),
      };
    } else {
      return selectedColumns;
    }
  };

  useEffect(() => {
    const value =
      normSelectionType === NORMALIZATION_TYPES.CUSTOM
        ? "selectColumns"
        : "ftnorm";

    setNormalizationOption(value);
    setNormalizationChanged(false);
  }, [normSelectionType, selectedTrain]);

  // Reset normalization option when sheet is changed
  useEffect(() => {
    if (Array.isArray(mappedColumn) && mappedColumn.length === 0) {
      setNormalizationOption("ftnorm");
      setNormalizationChanged(false);
    }
  }, [mappedColumn]);

  useEffect(() => {
    setSelectedColumns({
      ...mappedColumn,
    });
    if (
      mappedColumn &&
      mappedColumn.hasOwnProperty("norm_permeate_flow") &&
      mappedColumn.hasOwnProperty("norm_pressure_drop") &&
      mappedColumn.hasOwnProperty("norm_salt_passage")
    ) {
      setIsNormalizationColumnFilled(true);
    }
    if (!Array.isArray(mappedColumn)) {
      const col5 =
        mappedColumn && mappedColumn.concentrate_flowrate
          ? "concentrate_flowrate"
          : "feed_flowrate";

      const col8 =
        mappedColumn && mappedColumn.pressure_drop
          ? "pressure_drop"
          : "concentrate_pressure";

      setSelectedRadioColumn({
        ...selectedRadioColumn,
        column5: col5,
        column8: col8,
      });
    }
    if (isEdit && mappedReference) {
      setDataVisualisationEnabled(false);
    }
  }, [mappedColumn]);

  useEffect(() => {
    if (typeof config === "object" && !Array.isArray(config)) {
      const { sel_pass, sel_stage } = selectedColumns;
      if (config?.trains.length >= 1) {
        setTrainscolumns(
          TRAIN_OPTIONS(
            t,
            config,
            selectedTrain > 0 ? selectedTrain - 1 : "",
            sel_pass > 0 ? sel_pass - 1 : "",
          ),
        );
      }

      if (
        typeof selectedColumns === "object" &&
        !Array.isArray(selectedColumns)
      ) {
        let num_pressure_vessels = "";
        let sel_element_involved = "";
        let ro_nf_element_used = "";

        if (selectedTrain && sel_pass && sel_stage) {
          const train = config?.trains.find(
            (op) => op.train_id === parseInt(selectedTrain),
          );
          const pass = train?.passes.find(
            (pass) => pass.pass_id === parseInt(selectedColumns.sel_pass),
          );

          if (pass) {
            if (sel_stage === "All") {
              let totalPressureVessels = 0;
              let totalNoPressureVessels = 0;
              const allElements = pass.stages.flatMap(
                (stage) => stage.ro_nf_element_used,
              );
              const uniqueElements = [...new Set(allElements)];

              pass?.stages.forEach((element) => {
                totalPressureVessels += +element.num_pressure_vessels;
                const pressureVessels = +element.num_pressure_vessels;
                const numElementsPerPressureVessel =
                  +element.num_element_per_pressure_vessel;

                const currentNoPressureVessels =
                  pressureVessels * numElementsPerPressureVessel;
                const cumulativeNoPressureVessels =
                  currentNoPressureVessels + totalNoPressureVessels;

                totalNoPressureVessels = cumulativeNoPressureVessels;
              });
              num_pressure_vessels = totalPressureVessels;
              sel_element_involved = totalNoPressureVessels;
              ro_nf_element_used =
                uniqueElements.length === 1
                  ? uniqueElements[0]
                  : "multiple-elements";
            } else {
              const stage = pass?.stages.find(
                (stage) =>
                  stage.stage_id === parseInt(selectedColumns.sel_stage),
              );
              if (stage) {
                num_pressure_vessels = stage?.num_pressure_vessels || "";
                sel_element_involved =
                  stage?.num_pressure_vessels *
                    stage?.num_element_per_pressure_vessel || "";
                ro_nf_element_used = stage?.ro_nf_element_used || "";
                if (Array.isArray(ro_nf_element_used)) {
                  ro_nf_element_used = [...new Set(ro_nf_element_used)].join(
                    ", ",
                  );
                }
              } else {
                setSelectedColumns((prev) => ({
                  ...prev,
                  sel_stage: "",
                }));
                return;
              }
            }
          } else {
            setSelectedColumns((prev) => ({
              ...prev,
              sel_pass: "",
              sel_stage: "",
            }));
            return;
          }
        } else {
          num_pressure_vessels = "";
          sel_element_involved = "";
          ro_nf_element_used = "";
        }

        setSelectedColumns({
          ...selectedColumns,
          sel_pressure_vessels: num_pressure_vessels,
          sel_element_involved: sel_element_involved,
          sel_element_type: ro_nf_element_used,
        });
        // Track auto-calculated field changes
        setChangedPassStageFields({
          sel_pressure_vessels: num_pressure_vessels,
          sel_element_involved: sel_element_involved,
          sel_element_type: ro_nf_element_used,
        });
      }
    }
  }, [
    config,
    t,
    selectedColumns["sel_train"],
    selectedColumns["sel_pass"],
    selectedColumns["sel_stage"],
  ]);

  useEffect(() => {
    if (!fileId) {
      setSelectedColumns([]);
      setDataVisualisationEnabled(true);
      setDropdownColumns([]);
      setColumnOptions([]);
      setChangedSelectedColumns({});
      setChangedPassStageFields({});
      setNormalizationOption("ftnorm");
      setNormalizationChanged(false);
    }
  }, [fileId]);

  useEffect(() => {
    setChangedSelectedColumns({});
    setChangedPassStageFields({});
    setNormalizationChanged(false);
  }, [selectedTrain]);

  useEffect(() => {
    if (columnOptions && Object.keys(columnOptions).length > 0) {
      var filteredOptions = Object.values(columnOptions).filter((obj) => {
        return Object.values(selectedColumns).indexOf(obj) === -1;
      });
      setDropdownColumns(filteredOptions);
    }
  }, [selectedColumns, columnOptions, selectedRadioColumn]);

  const onDropdownValChange = (value, key) => {
    setDataVisualisationEnabled(true);

    setSelectedColumns({ ...selectedColumns, [key]: value });
    setChangedSelectedColumns((prevSet) =>
      UpdateSetWithKey(prevSet, key, value),
    );
  };

  const onTrainsValChange = (value, key) => {
    if (key === "sel_train") {
      value = value ? value.toString() : "";

      setSelectedColumns({
        ...selectedColumns,
        [key]: value,
        sel_pass: "",
        sel_stage: "",
      });
      setChangedSelectedColumns((prevSet) =>
        UpdateSetWithKey(prevSet, key, value),
      );
      setChangedSelectedColumns((prevSet) =>
        UpdateSetWithKey(prevSet, ["sel_pass"], ""),
      );
      setChangedSelectedColumns((prevSet) =>
        UpdateSetWithKey(prevSet, ["sel_stage"], ""),
      );
    } else if (key === "sel_pass") {
      setSelectedColumns({
        ...selectedColumns,
        [key]: value ?? "",
        sel_stage: "",
      });
      setChangedSelectedColumns((prevSet) =>
        UpdateSetWithKey(prevSet, key, value ?? ""),
      );
      setChangedSelectedColumns((prevSet) =>
        UpdateSetWithKey(prevSet, ["sel_stage"], ""),
      );
    } else {
      setSelectedColumns({
        ...selectedColumns,
        [key]: value ?? "",
      });
      setChangedSelectedColumns((prevSet) =>
        UpdateSetWithKey(prevSet, key, value ?? ""),
      );
    }
  };

  const getKey = (column) => {
    const key =
      column.options === undefined
        ? column.key
        : selectedRadioColumn[column.key];
    return key;
  };

  const handleNormalizationOptionChange = (newOption) => {
    const normalizedFields = [
      "norm_permeate_flow",
      "norm_pressure_drop",
      "norm_salt_passage",
    ];
    // Clear normalized columns in selectedColumns
    setSelectedColumns((prev) => ({
      ...prev,
      ...Object.fromEntries(normalizedFields.map((field) => [field, ""])),
    }));
    // Clear normalized columns in changedSelectedColumns
    setChangedSelectedColumns((prev) => ({
      ...prev,
      ...Object.fromEntries(normalizedFields.map((field) => [field, ""])),
    }));
    setNormalizationOption(newOption);
    setNormalizationChanged(true);
  };

  // Format options for WPDropdown (ensures {label, value} shape)
  const formatOptionsForWPDropdown = (options) =>
    (options || []).map((option) =>
      typeof option === "object" && option.label && option.value
        ? option
        : { label: option, value: option },
    );

  // Get dropdown options, excluding those already selected elsewhere
  const getDropdownOptions = (currentValue) => {
    if (!columnOptions) return [];
    const selectedValues = Object.values(selectedColumns);
    return formatOptionsForWPDropdown(
      Object.values(columnOptions).filter(
        (opt) => !selectedValues.includes(opt) || opt === currentValue,
      ),
    );
  };

  const handleOptionChange = (event) => {
    setDataVisualisationEnabled(true);
    const { name, value } = event.target;
    const prevKey = selectedRadioColumn[name];
    delete selectedColumns[prevKey];
    setSelectedRadioColumn({ ...selectedRadioColumn, [name]: value });
  };

  const getchecked = (column, key) => {
    return selectedRadioColumn[column] === key;
  };

  const openDataVisualizationPopup = async () => {
    setShowOverLayLoader(true);
    try {
      const response = await getDataVisualization(fileId, selectedTrain);
      if (response && response.data) {
        if (response.data.detail && response.data.detail.error) {
          throw new Error("DATA_VISUALIZATION_ERROR");
        } else {
          setDataVisualisationRow(response.data);
        }
      }
    } catch (err) {
      setDataVisualisationRow([]);
      createNotification(
        ERROR_NOTIFICATION,
        t("notificationStrings.somethingWrong"),
        "",
      );
    } finally {
      setShowOverLayLoader(false);
      return setshowDataVisualizationPopup(true);
    }
  };

  const onCloseClicked = async (value) => {
    setshowDataVisualizationPopup(false);
  };

  const checkAllRequiredFieldsFilled = () => {
    const {
      date,
      permeate_flowrate,
      feed_temp,
      permeate_pressure,
      feed_flowrate,
      concentrate_flowrate,
      permeate_quality,
      feed_pressure,
      feed_quality,
      concentrate_pressure,
      pressure_drop,
      norm_permeate_flow,
      norm_pressure_drop,
      norm_salt_passage,
    } = selectedColumns;

    const hasAllInputs = !!(
      date &&
      permeate_flowrate &&
      feed_temp &&
      permeate_pressure &&
      (feed_flowrate || concentrate_flowrate) &&
      permeate_quality &&
      feed_pressure &&
      feed_quality &&
      (concentrate_pressure || pressure_drop)
    );

    // If "Select Columns" is selected, normalized columns are mandatory
    if (normalizationOption === "selectColumns") {
      return (
        hasAllInputs &&
        !!(norm_permeate_flow && norm_pressure_drop && norm_salt_passage)
      );
    }

    return hasAllInputs;
  };

  const checkChangedFields = () => {
    return (
      Object.keys(changedSelectedColumns).length > 0 || normalizationChanged
    );
  };

  const getConfirmButtonState = () => {
    let disableConfirmButton = true;

    const hasAllRequiredFields = checkAllRequiredFieldsFilled();
    const hasChangedFields = checkChangedFields();

    if (hasAllRequiredFields && hasChangedFields) {
      disableConfirmButton = false;
    }

    return disableConfirmButton;
  };

  const onConfirmColumn = async () => {
    setShowOverLayLoader(true);
    try {
      const payload = {
        ...getOptimizedPayload(
          mappedColumn,
          changedSelectedColumns,
          changedPassStageFields,
          selectedColumns,
        ),
      };

      // Send norm_selection_type if: first time mapping (key not in mappedColumn) OR normalization was changed
      const isFirstTimeMapping =
        !mappedColumn || !mappedColumn.hasOwnProperty("norm_selection_type");
      if (isFirstTimeMapping || normalizationChanged) {
        payload.norm_selection_type =
          normalizationOption === "ftnorm"
            ? NORMALIZATION_TYPES.FT_NORM_PRO
            : NORMALIZATION_TYPES.CUSTOM;

        // Remove normalized columns from payload if FT Norm Pro is selected
        if (normalizationOption === "ftnorm") {
          delete payload.norm_permeate_flow;
          delete payload.norm_pressure_drop;
          delete payload.norm_salt_passage;
        }
      }

      const response = await updateColumnMapping(
        fileId,
        payload,
        selectedTrain,
      );
      if (response && response.detail === undefined) {
        setMappedColumn(selectedColumns);
        const { min_date, max_date } = response;
        setDatasetDetails(
          getDatasetDetails({
            owner_username: ownerUserName,
            plantName,
            water_type: waterType,
            min_date,
            max_date,
          }),
        );
        // Check if response.ref is empty, if so call getReference API
        if (!response.ref || response.ref === "") {
          try {
            const referenceResponse = await getReference(selectedTrain);
            if (referenceResponse) {
              setMappedReference(referenceResponse.data);
            } else {
              setMappedReference(response.ref);
            }
          } catch (referenceErr) {
            createNotification(
              ERROR_NOTIFICATION,
              t("notificationStrings.somethingWrong"),
              "",
            );
            setMappedReference(response.ref);
          }
        } else {
          setMappedReference(response.ref);
        }

        setDataVisualisationEnabled(true);
        setChangedSelectedColumns({});
        setChangedPassStageFields({});
        setNormalizationChanged(false);
        createNotification(
          SUCCESS_NOTIFICATION,
          t("analyseData.columnMapped"),
          "",
        );
        setDataVisualisationEnabled(false);
        if (
          payload.hasOwnProperty("norm_permeate_flow") &&
          payload.hasOwnProperty("norm_pressure_drop") &&
          payload.hasOwnProperty("norm_salt_passage")
        ) {
          setIsNormalizationColumnFilled(true);
        }
      } else if (
        response &&
        response.detail &&
        response.detail.key === COLUMN_MAPPING_ERROR_KEYS.NO_VALID_REF_CONDITION
      ) {
        setDataVisualisationEnabled(false);
        setChangedPassStageFields({});
        setChangedSelectedColumns({});
        createNotification(
          SUCCESS_NOTIFICATION,
          t("analyseData.columnMapped"),
          "",
        );
        createNotification(
          WARNING_NOTIFICATION,
          t("analyseData.noValidReferenceCondition"),
          "",
        );
      } else {
        throw new Error("UNKNOWN_ERROR");
      }
    } catch (err) {
      createNotification(
        ERROR_NOTIFICATION,
        t("notificationStrings.somethingWrong"),
        "",
      );
    } finally {
      setShowOverLayLoader(false);
    }
  };

  const getTrainValue = (value) => {
    if (value !== undefined && value !== null) {
      if (typeof value === "boolean" || typeof value === "number") {
        return value.toString();
      }
      return value;
    }
  };

  const handleImportColumnMapping = (
    columnMapping,
    normalizationColumnMapping,
  ) => {
    const changedColumns = {};

    columnMapping.forEach((mapping) => {
      if (columnOptions && columnOptions.includes(mapping.mappedTo)) {
        if (mapping.radioKey) {
          setSelectedRadioColumn((prev) => ({
            ...prev,
            [mapping.radioKey]: mapping.key,
          }));
        }
        changedColumns[mapping.key] = mapping.mappedTo;
      } else {
        changedColumns[mapping.key] = "";
      }
    });

    if (normalizationColumnMapping.length > 1) {
      setNormalizationOption("selectColumns");
      normalizationColumnMapping.forEach((mapping) => {
        if (columnOptions && columnOptions.includes(mapping.mappedTo)) {
          changedColumns[mapping.key] = mapping.mappedTo;
        } else {
          changedColumns[mapping.key] = "";
        }
      });
    }

    setChangedSelectedColumns(changedColumns);
    setSelectedColumns((prevSelectedColumns) => ({
      ...prevSelectedColumns,
      ...changedColumns,
    }));
    setShowImportColumnMappingPopup(false);
    if (Object.keys(changedColumns).length > 0) {
      createNotification(
        INFO_NOTIFICATION,
        t("columnSelection.importColumnsSuccess"),
        "",
      );
    } else {
      createNotification(
        WARNING_NOTIFICATION,
        t("columnSelection.noColumnsImported"),
        "",
      );
    }
  };

  const fetchColumnSelectionValidTrains = async () => {
    setTrainsWithColSelection([]);
    try {
      const response = await getPlantReadiness();
      if (response && response.data) {
        const plantData = response.data;
        if (plantData.length > 1) {
          const filteredData = plantData.filter(
            (trainData) =>
              trainData.column_mapping === true &&
              trainData.train !== selectedTrain,
          );

          if (filteredData.length > 0) {
            setTrainsWithColSelection(
              filteredData.map((trainData) => trainData.train),
            );
          }
        }
      }
    } catch {
      createNotification(
        ERROR_NOTIFICATION,
        t("notificationStrings.somethingWrong"),
        "",
      );
    }
  };

  useEffect(() => {
    if (selectedTrain && token) fetchColumnSelectionValidTrains();
  }, [selectedTrain, token]);

  useEffect(() => {
    const hasAllRequiredFields = checkAllRequiredFieldsFilled();
    const hasChangedFields = checkChangedFields();

    const columnMappingStatus = {
      fulfilled: hasAllRequiredFields,
      changed: hasChangedFields,
      error: false,
    };
    setMandatoryComponentsStatus((prevStatus) => ({
      ...prevStatus,
      columnMapping: columnMappingStatus,
    }));
  }, [
    selectedColumns,
    changedSelectedColumns,
    normalizationChanged,
    normalizationOption,
  ]);

  return (
    <div className="ro-card-wrapper position-relative">
      {showOverLayLoader && (
        <div className="circular-loader-overlay">
          <CircularLoader />
        </div>
      )}
      <div className="ro-card-header">
        <div className="info-section">
          <div className="teal-heading">{t("analyseData.columnMapping")}</div>
          <IconWithTooltip label={t("helpInformation.columnSelectionHelp")} />
        </div>
        <div>
          <WPTertiaryButton
            variant="text"
            label={t("columnSelection.importColumnMapping")}
            onClick={() => setShowImportColumnMappingPopup(true)}
            size="small"
            icon={<CopyIcon />}
            iconPlacement="before"
            disabled={!file_id || trainsWithColSelection.length === 0}
          />
          <WPTertiaryButton
            variant="text"
            label={t("analyseData.viewData")}
            onClick={openDataVisualizationPopup}
            disabled={dataVisualisationEnabled}
            size="small"
          />
          <WPTertiaryButton
            variant="outlined"
            icon={<CheckCircleIcon />}
            iconPlacement="before"
            label={t("ValueStrings.confirm")}
            disabled={getConfirmButtonState()}
            onClick={onConfirmColumn}
            size="small"
          />
        </div>
      </div>
      <div className="grid-container-trains">
        {Object.values(trainscolumns).map((column) => {
          const { key, name, options } = column;
          const isInputField = [
            "sel_pressure_vessels",
            "sel_element_involved",
            "sel_element_type",
          ].includes(key);

          // Skip input fields - they'll be rendered together below
          if (isInputField) return null;

          // Render Pass and Stage selects
          return (
            <div key={key} className="train-field-select">
              <WPDropdown
                label={name}
                options={formatOptionsForWPDropdown(options)}
                value={getTrainValue(selectedColumns[key])}
                onChange={(selectedOption) =>
                  onTrainsValChange(selectedOption?.value, key)
                }
                showCancelIcon
              />
            </div>
          );
        })}

        {/* Render grouped input fields */}
        <div className="train-fields-group">
          {[
            "sel_pressure_vessels",
            "sel_element_involved",
            "sel_element_type",
          ].map((fieldKey) => {
            const field = Object.values(trainscolumns).find(
              (col) => col.key === fieldKey,
            );
            if (!field) return null;

            const displayValue =
              fieldKey === "sel_element_type" &&
              selectedColumns[fieldKey] === "multiple-elements"
                ? t("ValueStrings.multipleElements")
                : selectedColumns[fieldKey] || "";

            return (
              <div key={fieldKey} className="train-field-input">
                <WPInput
                  id={fieldKey}
                  label={field.name}
                  value={displayValue}
                  disabled
                />
              </div>
            );
          })}
        </div>
      </div>
      <div className="container-padding-column">
        <div className="section-heading">
          {t("analysisColumn.feedPermeateDataColumns")}
        </div>
        <div className="grid-container-column">
          {Object.entries(columns).map(([columnKey, column]) => {
            const columnKeyValue = getKey(column);
            const selectedValue = selectedColumns[columnKeyValue];

            // Render columns with radio options
            if (column.options) {
              const options = Object.values(column.options);
              const radioLabels =
                columnKey === "column5"
                  ? ["Feed", "Concentrate"]
                  : ["dP", "Concentrate"];

              return (
                <div key={columnKey} className="grid-item-column-with-radio">
                  <div className="grid-item-column">
                    <WPDropdown
                      label={column.label}
                      value={selectedValue}
                      options={getDropdownOptions(selectedValue)}
                      onChange={(selectedOption) =>
                        onDropdownValChange(
                          selectedOption?.value,
                          columnKeyValue,
                        )
                      }
                      required
                      showCancelIcon
                    />
                  </div>
                  <div
                    className="radio-group-inline"
                    onChange={handleOptionChange}
                  >
                    {options.map((option, idx) => (
                      <DupontRadio
                        key={option.key}
                        text={radioLabels[idx]}
                        fontsize={"font-14"}
                        checked={getchecked(columnKey, option.key)}
                        value={option.key}
                        name={columnKey}
                      />
                    ))}
                  </div>
                </div>
              );
            }

            // Render regular columns
            return (
              <div key={columnKey} className="grid-item-column">
                <WPDropdown
                  label={column.name}
                  value={selectedValue}
                  options={getDropdownOptions(selectedValue)}
                  onChange={(selectedOption) =>
                    onDropdownValChange(selectedOption?.value, columnKeyValue)
                  }
                  required
                  showCancelIcon
                />
              </div>
            );
          })}
        </div>
      </div>

      <div className="margin-top-20">
        <div className="marginBtm20">
          <div className="normalization-title">
            {t("analysisColumn.normalizationTitle")}
          </div>
        </div>
        <div className="normalization-description">
          {t("analysisColumn.normalizationSectionDescription")}
        </div>

        <div
          className="normalization-radio-group"
          onChange={(event) => {
            const target = event.target;
            if (target.type === "radio") {
              handleNormalizationOptionChange(target.value);
            }
          }}
        >
          <DupontRadio
            text={t("analysisColumn.ftNormProLabel")}
            fontsize={"font-14"}
            checked={normalizationOption === "ftnorm"}
            value="ftnorm"
            name="normalization"
          />
          <DupontRadio
            text={t("analysisColumn.selectColumnsLabel")}
            fontsize={"font-14"}
            checked={normalizationOption === "selectColumns"}
            value="selectColumns"
            name="normalization"
          />
        </div>

        {normalizationOption === "ftnorm" ? (
          <div className="ftnorm-info-box">
            <div className="ftnorm-info-content">
              <InfoCircle />
              <span className="info-text">
                {t("analysisColumn.ftNormInfoText")}
              </span>
            </div>
            <FTNormProIcon />
          </div>
        ) : (
          <div>
            <div className="normalization-columns-grid">
              {Object.keys(normalizedColumns).map((normalizedColumn) => (
                <div
                  key={normalizedColumn}
                  className="normalization-column-item"
                >
                  <WPDropdown
                    label={normalizedColumns[normalizedColumn].name}
                    value={
                      selectedColumns[
                        getKey(normalizedColumns[normalizedColumn])
                      ]
                    }
                    options={getDropdownOptions(
                      selectedColumns[
                        getKey(normalizedColumns[normalizedColumn])
                      ],
                    )}
                    onChange={(selectedOption) =>
                      onDropdownValChange(
                        selectedOption?.value,
                        getKey(normalizedColumns[normalizedColumn]),
                      )
                    }
                    required
                    showCancelIcon
                  />
                </div>
              ))}
            </div>
            <div className="normalization-note">
              <InfoCircle />
              <span className="column-note-text">
                {t("analysisColumn.normalizationNote")}
              </span>
            </div>
          </div>
        )}
      </div>

      <div>
        {showDataVisualizationPopup && (
          <DataVisualizationTable
            open={showDataVisualizationPopup}
            title={t("analyseData.viewData")}
            rows={dataVisualisationRow}
            setshowDataVisualizationPopup={setshowDataVisualizationPopup}
            setTempReferenceValues={setTempReferenceValues}
            onClose={onCloseClicked}
            description={t("analyseData.dataVisualizationDescription")}
          />
        )}
        {showImportColumnMappingPopup && (
          <ImportColumnMapping
            trains={trainsWithColSelection}
            columns={columns}
            normalizedColumns={normalizedColumns}
            onImportColumnMapping={handleImportColumnMapping}
            onModalClose={() => setShowImportColumnMappingPopup(false)}
          />
        )}
      </div>
    </div>
  );
};
export default ColumnSelection;
